import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { Box, Button, Chip, CircularProgress, Paper } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import AcceptDecision from './AcceptDecision';
import {
  acceptMyOrder,
  cancelOrder,
  earlyEndOrder,
  fetchMyOrder,
  fetchOrder,
  rejectMyOrder,
} from './api';
import DangerButton from './DangerButton';
import EarlyEndClause from './EarlyEndClause';
import OrderAccess from './OrderAccess';
import { Field, TextBlock } from './OrderFields';
import { CancelOrderDialog, EarlyEndOrderDialog } from './OrderReasonDialogs';
import {
  OrderNotices,
  OrderNumberLink,
  OrderTimeline,
  OrderTripsField,
} from './OrderRecordSections';
import PageHeader from './PageHeader';
import { ApiResult, OrderDetails as OrderDetailsType } from './types';
import {
  canConsultantAccept,
  canConsultantReject,
  canManagerCancel,
  canManagerEndEarly,
  getAcceptedAt,
  getActionErrorDetail,
  getActionErrorMessage,
  getLifecycleStatusLabel,
  isEmploymentOrder,
  isOrderRenewable,
} from './utils';
import * as Styles from './Orders.css';

const getLoadErrorMessage = (error: 'forbidden' | 'notFound' | 'request') => {
  if (error === 'forbidden') {
    return 'ORDERS.NO_ACCESS';
  }
  if (error === 'notFound') {
    return 'ORDERS.NOT_FOUND';
  }
  return 'ORDERS.ERROR';
};

interface OrderDetailsContentProps {
  consultantView?: boolean;
  canManage?: boolean;
}

export const OrderDetailsContent = ({
  consultantView = false,
  canManage = false,
}: OrderDetailsContentProps) => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const intl = useIntl();
  const [details, setDetails] = useState<OrderDetailsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<
    'forbidden' | 'notFound' | 'request' | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionErrorDetail, setActionErrorDetail] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError(null);
    setActionError('');
    setActionErrorDetail('');
    const result = consultantView
      ? await fetchMyOrder(orderNumber)
      : await fetchOrder(orderNumber);
    if (requestId !== latestRequest.current) {
      return;
    }
    if (result.ok && result.data) {
      setDetails(result.data);
    } else if (result.status === 401 || result.status === 403) {
      setError('forbidden');
    } else if (result.status === 404) {
      setError('notFound');
    } else {
      setError('request');
    }
    setLoading(false);
  }, [consultantView, orderNumber]);

  useEffect(() => {
    load();
    return () => {
      latestRequest.current += 1;
    };
  }, [load]);

  const completeAction = async (
    request: Promise<ApiResult<OrderDetailsType>>,
    message: string,
  ) => {
    setActionLoading(true);
    setActionError('');
    setActionErrorDetail('');
    setSuccessMessage('');
    const result = await request;
    setActionLoading(false);
    if (result.ok && result.data) {
      setDetails(result.data);
      setSuccessMessage(message);
      setCancelOpen(false);
      setEndOpen(false);
      return;
    }
    setActionError(getActionErrorMessage(result.status));
    setActionErrorDetail(getActionErrorDetail(result.status, result.message));
    setCancelOpen(false);
    setEndOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Styles.Page>
        <Alert
          severity={error === 'request' ? 'error' : 'warning'}
          action={
            error === 'request' ? (
              <Button onClick={load}>
                {intl.formatMessage({ id: 'ORDERS.RETRY' })}
              </Button>
            ) : undefined
          }
        >
          {intl.formatMessage({ id: getLoadErrorMessage(error) })}
        </Alert>
      </Styles.Page>
    );
  }

  if (!details) {
    return null;
  }

  const { order, conditions, subject } = details;
  const events = details.events || [];
  const canAccept = consultantView && canConsultantAccept(order);
  const canReject = consultantView && canConsultantReject(order);
  const canCancel = canManage && canManagerCancel(order);
  const canEnd = canManage && canManagerEndEarly(order);
  const canRenew = canManage && isOrderRenewable(order);
  const employmentOrder = isEmploymentOrder(order);
  // Employment-contract orders are not accepted by anyone; "Issued at" covers them.
  const acceptedAt = employmentOrder ? null : getAcceptedAt(events);
  const notifyAlso = details.notifyAlso || [];
  const backUrl = consultantView ? '/my-assignments' : '/assignments';

  return (
    <Styles.Page>
      <PageHeader
        title={order.orderNumber}
        status={
          <Chip
            label={intl.formatMessage({ id: `ORDERS.STATUS.${order.status}` })}
            color={order.status === 'PENDING' ? 'secondary' : 'default'}
          />
        }
        actions={
          <>
            {canRenew && (
              <Button
                component={Link}
                to={`/assignments/${order.orderNumber}/extend`}
                color="primary"
                variant="outlined"
              >
                {intl.formatMessage({ id: 'ORDERS.RENEW' })}
              </Button>
            )}
            {canEnd && (
              <Button
                color="secondary"
                variant="outlined"
                disabled={actionLoading}
                onClick={() => setEndOpen(true)}
              >
                {intl.formatMessage({ id: 'ORDERS.END_EARLY' })}
              </Button>
            )}
            {canCancel && (
              <DangerButton
                disabled={actionLoading}
                onClick={() => setCancelOpen(true)}
              >
                {intl.formatMessage({ id: 'ORDERS.CANCEL_ORDER' })}
              </DangerButton>
            )}
            <Button component={Link} to={backUrl}>
              {intl.formatMessage({
                id: consultantView ? 'MY_ORDERS.BACK' : 'ORDERS.BACK',
              })}
            </Button>
          </>
        }
      />

      {successMessage && (
        <Box mb={2}>
          <Alert severity="success">
            {intl.formatMessage({ id: successMessage })}
          </Alert>
        </Box>
      )}
      {actionError && (
        <Box mb={2}>
          <Alert
            severity="error"
            action={
              actionError === 'ORDERS.ACTION_ERROR.CONFLICT' ? (
                <Button onClick={load}>
                  {intl.formatMessage({ id: 'ORDERS.RELOAD' })}
                </Button>
              ) : undefined
            }
          >
            {intl.formatMessage({ id: actionError })}
            {actionErrorDetail && <div>{actionErrorDetail}</div>}
          </Alert>
        </Box>
      )}

      <OrderNotices details={details} consultantView={consultantView} />

      <Paper>
        <Box px={3} py={2}>
          <Styles.DetailGrid>
            {!consultantView && (
              <Field
                label={intl.formatMessage({ id: 'ORDERS.CONSULTANT' })}
                value={order.consultantName}
              />
            )}
            <Field
              label={intl.formatMessage({ id: 'ORDERS.CLIENT' })}
              value={order.clientName}
            />
            <Field
              label={intl.formatMessage({ id: 'ORDERS.OWNER' })}
              value={order.ownerName}
            />
            <Field
              label={intl.formatMessage({ id: 'ORDERS.CREATED_BY' })}
              value={order.createdByName}
            />
            <Field
              label={intl.formatMessage({ id: 'ORDERS.CONTRACT_TYPE' })}
              value={intl.formatMessage({
                id: `ORDERS.CONTRACT.${order.contractType}`,
              })}
            />
            <Field
              label={intl.formatMessage({ id: 'ORDERS.LIFECYCLE_STATUS' })}
              value={intl.formatMessage({
                id: getLifecycleStatusLabel(
                  order.lifecycleStatus,
                  order.contractType,
                ),
              })}
            />
            <Field
              label={intl.formatMessage({ id: 'ORDERS.START_DATE' })}
              value={moment(order.startDate).format('DD/MM/YYYY')}
            />
            <Field
              label={intl.formatMessage({ id: 'ORDERS.END_DATE' })}
              value={moment(order.endDate).format('DD/MM/YYYY')}
            />
            <Field
              label={intl.formatMessage({ id: 'ORDERS.DEADLINE' })}
              value={moment(order.decisionDeadline).format('DD/MM/YYYY')}
            />
            {acceptedAt && (
              <Field
                label={intl.formatMessage({ id: 'ORDERS.ACCEPTED_AT' })}
                value={moment(acceptedAt).format('DD/MM/YYYY HH:mm')}
              />
            )}
            {order.activationDate && (
              <Field
                label={intl.formatMessage({ id: 'ORDERS.ACTIVATION_DATE' })}
                value={moment(order.activationDate).format('DD/MM/YYYY')}
              />
            )}
            {order.effectiveEndDate && (
              <Field
                label={intl.formatMessage({ id: 'ORDERS.EFFECTIVE_END_DATE' })}
                value={moment(order.effectiveEndDate).format('DD/MM/YYYY')}
              />
            )}
            <Field
              label={intl.formatMessage({ id: 'ORDERS.ISSUED_AT' })}
              value={moment(order.creationDateTime).format('DD/MM/YYYY HH:mm')}
            />
            {order.predecessorOrderNumber && (
              <Field
                label={intl.formatMessage({ id: 'ORDERS.PREDECESSOR' })}
                value={
                  <OrderNumberLink
                    number={order.predecessorOrderNumber}
                    consultantView={consultantView}
                  />
                }
              />
            )}
            {order.successorOrderNumber && (
              <Field
                label={intl.formatMessage({ id: 'ORDERS.SUCCESSOR' })}
                value={
                  <OrderNumberLink
                    number={order.successorOrderNumber}
                    consultantView={consultantView}
                  />
                }
              />
            )}
            {!consultantView && (
              <Field
                label={intl.formatMessage({ id: 'ORDERS.NOTIFY_ALSO' })}
                value={notifyAlso.map(({ name }) => name).join(', ')}
              />
            )}
            <OrderTripsField order={order} consultantView={consultantView} />
          </Styles.DetailGrid>
          {subject?.trim() && (
            <TextBlock
              label={intl.formatMessage({ id: 'ORDERS.SUBJECT' })}
              value={subject.trim()}
            />
          )}
          <TextBlock
            label={intl.formatMessage({ id: 'ORDERS.CONDITIONS' })}
            value={conditions?.trim() || '—'}
          />
          {consultantView && <EarlyEndClause />}
          <AcceptDecision
            orderNumber={order.orderNumber}
            canAccept={canAccept}
            canReject={canReject}
            busy={actionLoading}
            onAccept={() =>
              completeAction(
                acceptMyOrder(orderNumber),
                'ORDERS.SUCCESS.ACCEPT',
              )
            }
            onReject={(reason) =>
              completeAction(
                rejectMyOrder(orderNumber, { reason }),
                'ORDERS.SUCCESS.REJECT',
              )
            }
          />
        </Box>
      </Paper>

      <OrderTimeline details={details} consultantView={consultantView} />

      <CancelOrderDialog
        open={cancelOpen}
        busy={actionLoading}
        onClose={() => setCancelOpen(false)}
        onSubmit={(reason) =>
          completeAction(
            cancelOrder(orderNumber, { reason }),
            'ORDERS.SUCCESS.CANCEL',
          )
        }
      />
      <EarlyEndOrderDialog
        open={endOpen}
        busy={actionLoading}
        endDate={order.endDate}
        onClose={() => setEndOpen(false)}
        onSubmit={(request) =>
          completeAction(
            earlyEndOrder(orderNumber, request),
            'ORDERS.SUCCESS.EARLY_END',
          )
        }
      />
    </Styles.Page>
  );
};

const OrderDetails = () => (
  <OrderAccess>
    {({ canManage }) => <OrderDetailsContent canManage={canManage} />}
  </OrderAccess>
);

export default OrderDetails;
